import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  Calendar,
  Building2,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

interface RevenueSummary {
  totalOperations: number;
  totalGrossAmount: number;
  totalProviderCosts: number;
  totalPlatformFees: number;
  totalPlatformMargin: number;
  totalTenantFees: number;
  totalTenantMargin: number;
  totalPartnerCommissions: number;
  platformNetMargin: number;
  period: { start: string; end: string };
  byOperationType?: Array<{
    operationType: string;
    count: number;
    grossAmount: number;
    platformMargin: number;
    tenantMargin: number;
  }>;
  byTenant?: Array<{
    tenantId: string;
    tenantName?: string;
    count: number;
    grossAmount: number;
    platformMargin: number;
  }>;
}

interface TrendData {
  date?: string;
  month?: string;
  totalOperations: number;
  totalGrossAmount: number;
  totalMargin: number;
}

const COLORS = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#3b82f6'];

const OPERATION_LABELS: Record<string, string> = {
  PIX_RECEIVE: 'Pix Recebido',
  PIX_SEND: 'Pix Enviado',
  TRANSFER: 'Transferência',
  BOLETO: 'Boleto',
  CARD: 'Cartão',
  WITHDRAWAL: 'Saque',
  MAINTENANCE: 'Manutenção',
};

export function RevenueDashboard() {
  const [period, setPeriod] = useState('30d');
  const [viewType, setViewType] = useState<'daily' | 'monthly'>('daily');

  const { startDate, endDate, startMonth, endMonth } = useMemo(() => {
    const now = new Date();
    let start: Date;
    
    switch (period) {
      case '7d':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '12m':
        start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
      startMonth: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`,
      endMonth: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
    };
  }, [period]);

  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useQuery<RevenueSummary>({
    queryKey: ['revenue-summary', startDate, endDate],
    queryFn: async () => {
      const res = await api.get(ENDPOINTS.revenue.platformSummary, {
        params: { startDate, endDate },
      });
      return res.data;
    },
  });

  const { data: trendData, isLoading: trendLoading } = useQuery<TrendData[]>({
    queryKey: ['revenue-trend', viewType, startDate, endDate, startMonth, endMonth],
    queryFn: async () => {
      if (viewType === 'daily') {
        const res = await api.get(ENDPOINTS.revenue.dailyTrend, {
          params: { startDate, endDate },
        });
        return res.data;
      } else {
        const res = await api.get(ENDPOINTS.revenue.monthlyTrend, {
          params: { startMonth, endMonth },
        });
        return res.data;
      }
    },
  });

  const marginPercentage = summary && summary.totalPlatformFees > 0
    ? ((summary.platformNetMargin / summary.totalPlatformFees) * 100).toFixed(1)
    : '0';

  const operationChartData = summary?.byOperationType?.map((item) => ({
    name: OPERATION_LABELS[item.operationType] || item.operationType,
    value: item.platformMargin,
    count: item.count,
  })) || [];

  const tenantChartData = summary?.byTenant?.slice(0, 5).map((item) => ({
    name: item.tenantName || item.tenantId.slice(0, 8),
    margin: item.platformMargin,
    volume: item.grossAmount,
    count: item.count,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Revenue Dashboard</h1>
          <p className="text-surface-400 mt-1">
            Acompanhamento de receitas e margens da plataforma
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-32"
          >
            <option value="7d">7 dias</option>
            <option value="30d">30 dias</option>
            <option value="90d">90 dias</option>
            <option value="12m">12 meses</option>
          </Select>
          <Select
            value={viewType}
            onChange={(e) => setViewType(e.target.value as 'daily' | 'monthly')}
            className="w-32"
          >
            <option value="daily">Diário</option>
            <option value="monthly">Mensal</option>
          </Select>
          <Button
            variant="ghost"
            onClick={() => refetchSummary()}
            disabled={summaryLoading}
          >
            <RefreshCw className={`w-4 h-4 ${summaryLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Volume Total</p>
              <p className="text-2xl font-bold text-white mt-1">
                {formatCurrency(summary?.totalGrossAmount || 0)}
              </p>
              <p className="text-surface-500 text-xs mt-1">
                {summary?.totalOperations || 0} operações
              </p>
            </div>
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <DollarSign className="w-5 h-5 text-primary-400" />
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Receita Plataforma</p>
              <p className="text-2xl font-bold text-white mt-1">
                {formatCurrency(summary?.totalPlatformFees || 0)}
              </p>
              <p className="text-surface-500 text-xs mt-1">
                Taxas cobradas dos tenants
              </p>
            </div>
            <div className="p-2 bg-accent-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-accent-400" />
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Margem Líquida</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {formatCurrency(summary?.platformNetMargin || 0)}
              </p>
              <div className="flex items-center gap-1 mt-1">
                {Number(marginPercentage) >= 0 ? (
                  <ArrowUpRight className="w-3 h-3 text-green-400" />
                ) : (
                  <ArrowDownRight className="w-3 h-3 text-red-400" />
                )}
                <span className={`text-xs ${Number(marginPercentage) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {marginPercentage}% de margem
                </span>
              </div>
            </div>
            <div className="p-2 bg-green-500/20 rounded-lg">
              <BarChart3 className="w-5 h-5 text-green-400" />
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Custos Provider</p>
              <p className="text-2xl font-bold text-orange-400 mt-1">
                {formatCurrency(summary?.totalProviderCosts || 0)}
              </p>
              <p className="text-surface-500 text-xs mt-1">
                + {formatCurrency(summary?.totalPartnerCommissions || 0)} comissões
              </p>
            </div>
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <TrendingDown className="w-5 h-5 text-orange-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Chart */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary-400" />
              Evolução da Receita
            </h3>
          </div>
          <div className="h-64">
            {trendLoading ? (
              <div className="h-full flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-surface-400 animate-spin" />
              </div>
            ) : trendData && trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey={viewType === 'daily' ? 'date' : 'month'}
                    stroke="#9ca3af"
                    fontSize={12}
                    tickFormatter={(value) => {
                      if (viewType === 'daily') {
                        return new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                      }
                      return value;
                    }}
                  />
                  <YAxis
                    stroke="#9ca3af"
                    fontSize={12}
                    tickFormatter={(value) => `R$ ${(value / 100).toFixed(0)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [formatCurrency(Number(value) || 0), 'Margem']}
                    labelFormatter={(label) => {
                      if (viewType === 'daily') {
                        return new Date(label).toLocaleDateString('pt-BR');
                      }
                      return label;
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="totalMargin"
                    stroke="#06b6d4"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-surface-400">
                Sem dados para o período selecionado
              </div>
            )}
          </div>
        </Card>

        {/* Operation Type Distribution */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-accent-400" />
              Margem por Operação
            </h3>
          </div>
          <div className="h-64">
            {summaryLoading ? (
              <div className="h-full flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-surface-400 animate-spin" />
              </div>
            ) : operationChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={operationChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {operationChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value, name, props) => [
                      `${formatCurrency(Number(value) || 0)} (${props?.payload?.count || 0} ops)`,
                      String(name),
                    ]}
                  />
                  <Legend
                    verticalAlign="middle"
                    align="right"
                    layout="vertical"
                    wrapperStyle={{ fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-surface-400">
                Sem dados para o período selecionado
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Tenants */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary-400" />
              Top 5 Tenants por Margem
            </h3>
          </div>
          <div className="h-64">
            {summaryLoading ? (
              <div className="h-full flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-surface-400 animate-spin" />
              </div>
            ) : tenantChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tenantChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    type="number"
                    stroke="#9ca3af"
                    fontSize={12}
                    tickFormatter={(value) => `R$ ${(value / 100).toFixed(0)}`}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#9ca3af"
                    fontSize={12}
                    width={80}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [formatCurrency(Number(value) || 0), 'Margem']}
                  />
                  <Bar dataKey="margin" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-surface-400">
                Sem dados para o período selecionado
              </div>
            )}
          </div>
        </Card>

        {/* Cascade Breakdown */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-accent-400" />
              Breakdown da Cascata
            </h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-surface-300">Volume Bruto</span>
              </div>
              <span className="text-white font-medium">
                {formatCurrency(summary?.totalGrossAmount || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="text-surface-300">(-) Custos Provider</span>
              </div>
              <span className="text-orange-400 font-medium">
                -{formatCurrency(summary?.totalProviderCosts || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-cyan-500" />
                <span className="text-surface-300">Taxas Plataforma</span>
              </div>
              <span className="text-cyan-400 font-medium">
                {formatCurrency(summary?.totalPlatformFees || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-purple-500" />
                <span className="text-surface-300">(-) Comissões Parceiros</span>
              </div>
              <span className="text-purple-400 font-medium">
                -{formatCurrency(summary?.totalPartnerCommissions || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-green-300 font-medium">= Margem Líquida</span>
              </div>
              <span className="text-green-400 font-bold text-lg">
                {formatCurrency(summary?.platformNetMargin || 0)}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default RevenueDashboard;
